import { AlertTriangle, ArrowRightLeft, Users2, Shuffle, Siren, CheckCircle2 } from "lucide-react";
import { KINDS } from "@/lib/constants";
import { DetourFeedbackButton } from "@/components/plan/DetourFeedback";
import { estado } from "@/lib/estadosDesvio";

const Stat = ({ label, value, sub, icon: Icon, tone = "text-sky-600", testid }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between"><span className="font-mono text-[0.625rem] tracking-widest text-slate-500 uppercase">{label}</span><Icon className={`w-4 h-4 ${tone}`} /></div>
    <div className="font-display text-2xl font-bold mt-2">{value}</div>
    {sub && <div className="text-[0.6875rem] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export const PlanSummary = ({ plan, elements }) => {
  const s = plan.summary;
  return (
    <div className="space-y-6" data-testid="plan-summary">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Efectivos usados" value={s.agents_used} sub={`sin relevos harían falta ${s.agents_without_reuse}`} icon={Users2} testid="plan-agents-used" />
        <Stat label="Traslados" value={s.relocations} sub={`${s.shift_handovers ?? 0} relevos por cambio de turno`} icon={Shuffle} tone="text-emerald-600" testid="plan-relocations" />
        <Stat label="Pico policial" value={s.peak_police_need} sub="agentes simultáneos regulando" icon={Siren} tone="text-red-600" testid="plan-peak-police" />
        <Stat label="Tareas" value={s.total_tasks} sub={`${plan.detours.filter((d) => d.coords?.length).length} desvíos calculados`} icon={ArrowRightLeft} testid="plan-total-tasks" />
        <Stat label="Déficit" value={s.missing_agents} sub={s.deficit_count ? `${s.deficit_count} tareas sin cubrir` : "todo cubierto"}
          icon={s.missing_agents ? AlertTriangle : CheckCircle2} tone={s.missing_agents ? "text-amber-600" : "text-emerald-600"} testid="plan-deficit" />
      </div>

      {plan.deficits.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-xl p-4" data-testid="plan-deficits-list">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Puestos sin cubrir</div>
          {plan.deficits.map((d) => (
            <div key={d.task_id} className="text-xs text-slate-700 py-1 border-t border-slate-200">
              <span className="font-mono text-amber-700">{d.start}–{d.end}</span> · {d.name} · faltan <b>{d.missing}</b> ({d.allowed.join(" / ")})
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="font-mono text-[0.625rem] tracking-widest text-slate-500 uppercase mb-2">Horarios calculados</div>
        <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="plan-timings-table">
          {elements.filter((e) => plan.timings[e.id]).map((e) => {
            const t = plan.timings[e.id];
            return (
              <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-2 text-xs border-b border-slate-200 items-center">
                <span className="col-span-2 font-mono text-[0.625rem]" style={{ color: KINDS[e.kind].color }}>{KINDS[e.kind].short}</span>
                <span className="col-span-5 text-slate-800">{e.name}</span>
                <span className="col-span-3 font-mono text-slate-700">{t.start_hm} – {t.end_hm}</span>
                <span className="col-span-2 text-slate-500">{t.source}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const DetoursList = ({ detours, eventId, canEdit, overrides = {}, sentIds = [], onSent }) => (
  <div className="grid md:grid-cols-2 gap-3" data-testid="detours-list">
    {detours.length === 0 && <div className="text-sm text-slate-500">No hay cortes con desvío.</div>}
    {detours.map((d) => (
      <div key={d.element_id} className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid={`detour-card-${d.element_id}`}>
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">{d.element_name}{d.pending && <span className="ml-2 text-[0.625rem] text-rose-700 font-normal">Propuesta · acepte el corte previo</span>}</div>
          <span className={`text-[0.625rem] px-2 py-0.5 rounded-full border ${estado(d.status).cls}`}>
            {estado(d.status).label}
          </span>
        </div>
        {d.coords?.length ? (
          <>
            <div className="font-mono text-[0.6875rem] text-slate-500 mt-1">{d.distance_m} m · ~{d.duration_min} min en vehículo</div>
            <div className="text-xs text-slate-700 mt-2 leading-relaxed">{d.from && <b>Desde {d.from}: </b>}{d.streets.length ? d.streets.join(" → ") : "Vía sin nombre"}</div>
            {d.branches?.map((b, i) => (
              <div key={i} className="text-xs text-slate-700 mt-1 leading-relaxed" data-testid={`detour-branch-${d.element_id}-${i}`}><b>Desde {b.from}: </b>{b.streets.join(" → ") || "Vía sin nombre"} <span className="font-mono text-[0.625rem] text-slate-500">({b.distance_m} m)</span></div>
            ))}
            {d.note && d.coords?.length > 0 && <div className="text-[0.6875rem] text-slate-500 mt-2">{d.note}</div>}
            {d.options?.length > 0 && <div className="text-[0.6875rem] text-slate-500 mt-1">Otras salidas posibles: {d.options.join(", ")}</div>}
            {canEdit && eventId && !d.pending && <div className="mt-3"><DetourFeedbackButton eventId={eventId} d={d} edited={!!overrides[d.element_id]} sent={sentIds.includes(d.element_id)} onSent={onSent} /></div>}
            {d.special_name && <div className="text-[0.6875rem] text-purple-700 mt-2 bg-purple-500/10 border border-purple-500/30 rounded px-2 py-1" data-testid={`detour-special-${d.element_id}`}>Usa zona de tránsito especial: <b>{d.special_name}</b> (habilitar provisionalmente)</div>}
            {d.reversed_name && <div className="text-[0.6875rem] text-pink-700 mt-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1" data-testid={`detour-reversed-${d.element_id}`}>Circula por calle con sentido invertido: <b>{d.reversed_name}</b> (regulación en entrada y salida)</div>}
            {d.oneway_conflicts?.length > 0 && (
              <div className="text-[0.6875rem] text-red-700 mt-2 bg-red-500/10 border border-red-500/40 rounded px-2 py-1.5" data-testid={`detour-oneway-warning-${d.element_id}`}>
                <b>Contra sentido de circulación:</b> {d.oneway_conflicts.map((c) => `${c.name} (${c.length_m} m)`).join(", ")}. Marcado en rojo en el mapa. Valore invertir el sentido provisionalmente con regulación o buscar otro itinerario.
              </div>
            )}
            {d.oneway_checked && !d.oneway_conflicts?.length && <div className="text-[0.625rem] text-teal-700 mt-2" data-testid={`detour-oneway-ok-${d.element_id}`}>Sentidos de circulación comprobados: sin tramos a contra dirección.</div>}
            {d.oneway_checked === false && <div className="text-[0.625rem] text-amber-700 mt-2">No se pudieron comprobar los sentidos (OpenStreetMap no disponible).</div>}
            {d.status === "parcial" && <div className="text-[0.6875rem] text-lime-700 mt-2">El desvío atraviesa el recorrido: habilitar paso cuando haya pasado la comitiva.</div>}
          </>
        ) : d.status === "sin_servicio" ? (
          <div className="text-xs text-amber-700 mt-2" data-testid={`detour-service-error-${d.element_id}`}>El servidor de rutas no respondió. Pulse “Recalcular plan” en unos segundos.</div>
        ) : ["en_recorrido", "sin_calle"].includes(d.status) ? (
          <div className="text-xs text-slate-600 mt-2" data-testid={`detour-note-${d.element_id}`}>{d.note}</div>
        ) : (
          <div className="text-xs text-slate-500 mt-2" data-testid={`detour-note-${d.element_id}`}>{d.note || "No se ha encontrado ruta alternativa."} Marque en el mapa una <b className="text-purple-700">Zona de tránsito especial</b> que pueda habilitarse y recalcule, o valore acceso solo residentes.</div>
        )}
      </div>
    ))}
  </div>
);

export const SignageTable = ({ signage, parking }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <div>
      <div className="font-mono text-[0.625rem] tracking-widest text-slate-500 uppercase mb-2">Inventario de señalización y material</div>
      <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="signage-table">
        {!signage.length && <div className="px-4 py-4 text-xs text-slate-500" data-testid="signage-empty">No hay objetos de señalización colocados en el mapa. Añádalos desde «Objetos»: aquí solo aparece lo que usted coloca.</div>}
        {signage.map((s) => (
          <div key={s.code} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-slate-200 text-xs items-start">
            <span className="col-span-2 font-mono text-amber-700">{s.code}</span>
            <span className="col-span-7 text-slate-800">{s.name}<div className="text-[0.625rem] text-slate-500 mt-0.5">{s.where.join(" · ")}</div></span>
            <span className="col-span-3 text-right font-display text-lg font-bold">{s.qty}</span>
          </div>
        ))}
      </div>
    </div>
    <div>
      <div className="font-mono text-[0.625rem] tracking-widest text-slate-500 uppercase mb-2">Reservas de estacionamiento</div>
      <div className="space-y-2" data-testid="parking-list">
        {parking.length === 0 && <div className="text-sm text-slate-500">Sin zonas de retirada de vehículos.</div>}
        {parking.map((p) => (
          <div key={p.element_id} className="bg-slate-50 border border-amber-500/30 rounded-xl p-4">
            <div className="font-semibold text-sm">{p.name}</div>
            <div className="font-mono text-[0.6875rem] text-slate-500 mt-1">Prohibido {p.from} – {p.to} · perímetro {p.perimeter_m} m</div>
            <div className="text-xs text-slate-700 mt-2">Capacidad estimada: <b>~{p.est_vehicles} vehículos</b>. Señalizar con R-308 y horario con 48 h de antelación; prever grúa municipal 30 min antes.</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
